import { useEffect, useMemo } from 'react';
import { useDecryptedApplicationCredentials } from '../../../hooks/api/useDecryptedApplicationCredentials';
import { useCredentialEditStore } from '../../../store/credentialEditStore';
import { useCredentialAuthMaterialEditStore } from '../../../store/credentialAuthMaterialEditStore';
import { useApplicationCopilotEditContextStore } from '../../../store/applicationCopilotEditContextStore';
import type { BackendCredential } from './applicationCredential.transforms';

const NO_CREDENTIALS: BackendCredential[] = [];

const hasPendingFields = (
  pendingEdits: Record<string, { fields: unknown[] } | undefined>,
  credentialId: string,
) => (pendingEdits[credentialId]?.fields.length ?? 0) > 0;

export function useTargetCredentialsState({
  applicationId,
  tenantId,
}: {
  applicationId?: string;
  tenantId?: string;
}) {
  const { data: credentialsData, isLoading: credentialsLoading } =
    useDecryptedApplicationCredentials({ applicationId, tenantId });
  const credentials = useMemo(
    () => credentialsData?.credentials ?? NO_CREDENTIALS,
    [credentialsData],
  );

  useEffect(() => {
    useApplicationCopilotEditContextStore.getState().setCredentialCatalog(
      credentials.map((credential) => ({
        id: credential.id,
        role: credential.role,
        roleId: credential.roleId ?? null,
        roleDescription: credential.roleDescription ?? null,
        headers: credential.headers,
        cookies: credential.cookies,
      })),
    );
  }, [credentials]);

  const credentialPendingEdits = useCredentialEditStore(
    (state) => state.pendingAiEdits,
  );
  const credentialAuthMaterialPendingEdits = useCredentialAuthMaterialEditStore(
    (state) => state.pendingAiEdits,
  );

  useEffect(() => {
    const pendingCredentialIds = [
      ...new Set([
        ...Object.keys(credentialPendingEdits).filter((credentialId) =>
          hasPendingFields(credentialPendingEdits, credentialId),
        ),
        ...Object.keys(credentialAuthMaterialPendingEdits).filter(
          (credentialId) =>
            hasPendingFields(credentialAuthMaterialPendingEdits, credentialId),
        ),
      ]),
    ];

    if (pendingCredentialIds.length === 0 || credentials.length === 0) {
      return;
    }

    const { syncCredentialEditBaseFields } = useCredentialEditStore.getState();
    const { syncCredentialAuthMaterialEditBaseFields } =
      useCredentialAuthMaterialEditStore.getState();

    pendingCredentialIds.forEach((credentialId) => {
      const credential = credentials.find((entry) => entry.id === credentialId);

      if (!credential) {
        return;
      }

      if (hasPendingFields(credentialPendingEdits, credentialId)) {
        syncCredentialEditBaseFields(credentialId, {
          role: credential.role,
          roleId: credential.roleId ?? null,
          roleDescription: credential.roleDescription ?? null,
        });
      }

      if (hasPendingFields(credentialAuthMaterialPendingEdits, credentialId)) {
        syncCredentialAuthMaterialEditBaseFields(credentialId, {
          headers: credential.headers,
          cookies: credential.cookies,
        });
      }
    });
  }, [credentialPendingEdits, credentialAuthMaterialPendingEdits, credentials]);

  return {
    credentials,
    credentialsLoading,
  };
}
